import { type JSX, ReactNode } from "react";

import Card from "@mui/material/Card";
import {
  ThumbnailActionArea,
  ThumbnailValueComponentsProvider,
} from "@plansoft-configuration-renderer/components";
import { useLocalOptionSelected } from "@plansoft-configuration-renderer/framework";

import { ImageInfoOverride } from "./ImageInfoOverride";

export interface ThumbnailItemProps {
  children: ReactNode;
}

/**
 * Base width of a thumbnail card and its image. The card never shrinks below
 * this; it only grows when a label contains a word too long to fit, so text is
 * never clipped. The image stays pinned to this width regardless of card width.
 */
const THUMBNAIL_WIDTH = 150;

/**
 * Renders basic wrapper around the content
 */
export function ThumbnailItem(props: ThumbnailItemProps): JSX.Element {
  // there is a isSelected value but it does not work with local values
  const isSelected = useLocalOptionSelected();

  // Note: do not change "elevation" property of Card/Paper since it interferes with "raised"
  return (
    <Card
      raised={isSelected}
      sx={{
        height: "100%", // height controlled by the grid; aligns all cards in a row to the tallest
        // Hug content: stay at the base width but grow to fit any label word too
        // long to wrap, so text is never clipped.
        width: "min-content",
        minWidth: THUMBNAIL_WIDTH,
        // Keep the image at the base width even when the card grows for text,
        // and center it horizontally within the (possibly wider) card.
        "& .MuiCardMedia-root": { width: THUMBNAIL_WIDTH, mx: "auto" },
        "& .MuiCardActionArea-root": { position: "relative" },
        "& .MuiCardActions-root": {
          display: "block",
          position: "absolute",
          bottom: 0,
          right: 0,
          p: 0,
          m: 0,
          "& .MuiIconButton-root": { p: 0.25 },
          "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
        },
      }}
    >
      <ThumbnailValueComponentsProvider>
        <ImageInfoOverride>
          <ThumbnailActionArea>{props.children}</ThumbnailActionArea>
        </ImageInfoOverride>
      </ThumbnailValueComponentsProvider>
    </Card>
  );
}
